import React, { useContext, createContext, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { v4 } from 'uuid';
import Button from '@prism/button';
import Icon from '@prism/icon';
import Tippy from '@tippyjs/react';
import { useClickAway } from 'react-use';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

// check for dead vars
var CENTER = 'center';
var LEFT = 'left';
var RIGHT = 'right';
var CNAME = 'actions';
var CNAME_ACTION = "".concat(CNAME, "-action");
var CNAME_BUTTON = 'btn';
var CNAME_ICON = "".concat(CNAME, "-icon");
var CNAME_ICON_ONLY = "".concat(CNAME, "-icon-only");
var CNAME_INLINE = "".concat(CNAME, "-inline");
var CNAME_LABEL = "".concat(CNAME, "-label");
var CNAME_MASK = "".concat(CNAME, "-mask");
var CNAME_MORE = "".concat(CNAME, "-more");
var CNAME_PANEL = "".concat(CNAME, "-panel");
var CNAME_SPACER = "".concat(CNAME, "-spacer");
var CNAME_TEXT_ONLY = "".concat(CNAME, "-text-only");
var MORE_LABEL = 'More';
var SHOW_ALL = 'all';
var VARIANT_PANEL_LIST = 'panel-list';
var VARIANT_LIST = 'list';

var initialState = {
  action: null,
  actions: [],
  iconOnly: false,
  menuLabel: MORE_LABEL,
  remainingActions: [],
  show: 2,
  showPanel: false,
  textOnly: false,
  visibleActions: []
};

var Context = /*#__PURE__*/createContext([initialState, {}]);
var useActionsContext = function useActionsContext() {
  return useContext(Context);
};

var _excluded = ["actionIndex"],
    _excluded2 = ["addOnAppend", "addOnPrepend", "className", "iconOnly", "inline", "label", "textOnly"];
var propTypes$2 = {
  actionIndex: PropTypes.number
};
var defaultProps$2 = {
  actionIndex: 0
};

var Action = function Action(_ref) {
  var _classnames;

  var actionIndex = _ref.actionIndex,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useActionsContext = useActionsContext(),
      _useActionsContext2 = _slicedToArray(_useActionsContext, 2),
      _useActionsContext2$ = _useActionsContext2[1].getActionState,
      getActionState = _useActionsContext2$ === void 0 ? function () {
    return {};
  } : _useActionsContext2$;

  var _getActionState$props = _objectSpread2(_objectSpread2({}, getActionState(actionIndex)), props),
      addOnAppend = _getActionState$props.addOnAppend,
      addOnPrepend = _getActionState$props.addOnPrepend,
      propClassName = _getActionState$props.className,
      iconOnly = _getActionState$props.iconOnly,
      inline = _getActionState$props.inline,
      label = _getActionState$props.label,
      textOnly = _getActionState$props.textOnly,
      attrs = _objectWithoutProperties(_getActionState$props, _excluded2);

  var iconNode = function iconNode(glyph, labelText) {
    var className = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    return /*#__PURE__*/React.createElement(Icon, {
      className: classnames(CNAME_ICON, className),
      glyph: glyph,
      title: labelText
    });
  };

  var className = classnames(CNAME_ACTION, CNAME_BUTTON, (_classnames = {}, _defineProperty(_classnames, CNAME_ICON_ONLY, iconOnly), _defineProperty(_classnames, CNAME_TEXT_ONLY, textOnly), _defineProperty(_classnames, CNAME_INLINE, inline), _classnames), propClassName);
  return /*#__PURE__*/React.createElement(Button, _extends({
    variant: "actionLink",
    "aria-label": label || null,
    className: className
  }, attrs), !textOnly && addOnPrepend ? iconNode(addOnPrepend, label) : null, !iconOnly && label ? /*#__PURE__*/React.createElement("span", {
    className: CNAME_LABEL
  }, label) : null, !textOnly && addOnAppend ? iconNode(addOnAppend, label) : null);
};

Action.propTypes = propTypes$2;
Action.defaultProps = defaultProps$2;
Action.displayName = 'Action';

var propTypes$1 = {
  iconOnly: PropTypes.bool,
  onClick: PropTypes.func
};
var defaultProps$1 = {
  iconOnly: false,
  onClick: function onClick() {}
}; // `forwardRef` required below by Tippy.js as the `panelRef` is passed to a child
//   prop component. https://reactjs.org/docs/forwarding-refs.html

var PanelTrigger = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var menuLabel = _ref.menuLabel,
      propIconOnly = _ref.iconOnly,
      onClick = _ref.onClick;

  var _useActionsContext = useActionsContext(),
      _useActionsContext2 = _slicedToArray(_useActionsContext, 2),
      _useActionsContext2$ = _useActionsContext2[0],
      _useActionsContext2$$ = _useActionsContext2$.iconOnly,
      iconOnly = _useActionsContext2$$ === void 0 ? propIconOnly : _useActionsContext2$$,
      showPanel = _useActionsContext2$.showPanel,
      setShowPanel = _useActionsContext2[1].setShowPanel;

  var handlePanelTriggerClick = function handlePanelTriggerClick(e) {
    var newShowPanel = !showPanel;

    if (setShowPanel) {
      setShowPanel(newShowPanel);
    }

    if (onClick) {
      onClick(_objectSpread2(_objectSpread2({}, e), {}, {
        showPanel: newShowPanel
      }));
    }
  };

  var DOTS = 'three-dots-vertical';
  var ARROW_UP = 'chevron-up';
  var ARROW_DOWN = 'chevron-down';

  var _ref2 = iconOnly ? [DOTS, DOTS] : [ARROW_DOWN, ARROW_UP],
      _ref3 = _slicedToArray(_ref2, 2),
      showIcon = _ref3[0],
      hideIcon = _ref3[1];

  return /*#__PURE__*/React.createElement(Action, {
    actionIndex: -1,
    className: CNAME_MORE,
    addOnAppend: showPanel ? hideIcon : showIcon,
    iconOnly: iconOnly,
    label: menuLabel,
    innerRef: ref,
    onClick: handlePanelTriggerClick
  });
});
PanelTrigger.propTypes = propTypes$1;
PanelTrigger.defaultProps = defaultProps$1;
PanelTrigger.displayName = 'PanelTrigger';

var propTypes = {
  align: PropTypes.oneOf([CENTER, LEFT, RIGHT]),
  className: PropTypes.string,
  children: PropTypes.node,
  showPanel: PropTypes.bool,
  hideOnSelect: PropTypes.bool,
  panelRef: PropTypes.oneOfType([PropTypes.func, PropTypes.node, PropTypes.shape({
    current: PropTypes.ref
  })]),
  iconOnly: PropTypes.bool,
  menuLabel: PropTypes.string
};
var defaultProps = {
  align: LEFT,
  iconOnly: false,
  className: '',
  children: null,
  showPanel: false,
  panelRef: null,
  menuLabel: MORE_LABEL
};

var Panel = function Panel(_ref) {
  var align = _ref.align,
      iconOnly = _ref.iconOnly,
      propClassName = _ref.className,
      children = _ref.children,
      menuLabel = _ref.menuLabel,
      panelRef = _ref.panelRef,
      hideOnSelect = _ref.hideOnSelect,
      propShowPanel = _ref.showPanel;
  var ref = React.useRef(panelRef);

  var _React$useState = React.useState(propShowPanel),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      visible = _React$useState2[0],
      toggle = _React$useState2[1];

  useClickAway(ref, function () {
    if (visible) {
      toggle(!visible);
    }
  });
  var className = classnames(CNAME_PANEL, {
    'dropdown-menu': true,
    show: visible,
    'align-center': align === CENTER,
    'align-left': align === LEFT,
    'align-right': align === RIGHT
  }, propClassName);

  var getPlacement = function getPlacement() {
    switch (align) {
      case CENTER:
        return 'bottom';

      case LEFT:
        return 'bottom-start';

      case RIGHT:
        return 'bottom-end';

      default:
        return 'auto';
    }
  };

  var popperOptions = {
    modifiers: [{
      name: 'flip',
      options: {
        allowedAutoPlacements: [CENTER, LEFT, RIGHT].includes(align) ? ['bottom-start', 'bottom-end', 'top-start', 'top-end'] : undefined
      }
    }]
  };
  var appendTarget = typeof document !== 'undefined' ? document.body : 'parent';

  var hidePanel = function hidePanel() {
    return hideOnSelect && toggle(!visible);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Tippy, {
    interactive: true,
    offset: 0,
    popperOptions: popperOptions,
    placement: getPlacement(),
    appendTo: appendTarget,
    render: function render(attrs) {
      return visible ? /*#__PURE__*/React.createElement("div", {
        className: "prism-sandbox prism-sandbox"
      }, /*#__PURE__*/React.createElement("div", _extends({
        className: className,
        ref: ref,
        role: "group"
      }, attrs), React.Children.toArray(children).map(function (child) {
        return /*#__PURE__*/React.createElement("li", {
          onClick: hidePanel,
          key: "actions-panel-".concat(child.key)
        }, child);
      }))) : null;
    },
    visible: !!visible
  }, /*#__PURE__*/React.createElement(PanelTrigger, {
    onClick: function onClick() {
      return toggle(!visible);
    },
    menuLabel: menuLabel,
    iconOnly: iconOnly,
    showPanel: visible
  })), visible && /*#__PURE__*/React.createElement("span", {
    className: CNAME_MASK
  }));
};

Panel.propTypes = propTypes;
Panel.defaultProps = defaultProps;
Panel.displayName = 'Panel';

var Spacer = function Spacer() {
  return /*#__PURE__*/React.createElement("span", {
    className: CNAME_SPACER,
    role: "none"
  });
};

var Actions = function Actions(_ref) {
  var action = _ref.action,
      actions = _ref.actions,
      children = _ref.children,
      propClassName = _ref.className,
      id = _ref.id,
      menuAlignment = _ref.menuAlignment,
      show = _ref.show,
      iconOnly = _ref.iconOnly,
      menuLabel = _ref.menuLabel,
      variant = _ref.variant,
      hideOnSelect = _ref.hideOnSelect;
  var className = classnames(CNAME, propClassName, _defineProperty({}, "variant-".concat(variant), !!variant));

  var _useMemo = useMemo(function () {
    // convert 'show' prop to valid integer
    var visibleCount = typeof show === 'number' ? show : -1; // actions provided as prop with child <Action /> props;

    var childArgs = React.Children.toArray(children).map(function (c) {
      return c.props;
    }); // merge prop provided actions with child actions

    var compositeArgs = [action].concat(_toConsumableArray(actions), _toConsumableArray(childArgs)); // remove empty objects and assign indeces

    var all = compositeArgs.filter(Boolean).map(function (item, idx) {
      return _objectSpread2(_objectSpread2({}, item), {}, {
        actionIndex: idx
      });
    }); // provide the full list, visible list and expanded list.

    return {
      all: all,
      // every action
      visible: all.slice(0, visibleCount),
      // displayed actions
      extra: all.slice(visibleCount),
      // dropdown actions
      hasExtra: all.length > visibleCount // helper boolean

    };
  }, [show, children, action, actions]),
      visible = _useMemo.visible,
      hasExtra = _useMemo.hasExtra,
      extra = _useMemo.extra;

  function toAction(act) {
    // render a <Spacer /> if desired
    if (act.spacer) return /*#__PURE__*/React.createElement(Spacer, _extends({}, act, {
      key: "".concat(id, "-spacer-").concat(act.actionIndex)
    })); // render an <Action /> if theres a label included

    if (act.label) return /*#__PURE__*/React.createElement(Action, _extends({}, act, {
      key: "".concat(id, "-action-").concat(act.actionIndex)
    })); // do nothing otherwise

    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, visible.map(toAction), hasExtra && /*#__PURE__*/React.createElement(Panel, {
    align: menuAlignment,
    hideOnSelect: hideOnSelect,
    iconOnly: iconOnly,
    menuLabel: menuLabel
  }, extra.map(toAction)));
};

Actions.propTypes = {
  action: PropTypes.shape({
    addOnPrepend: PropTypes.string,
    label: PropTypes.string,
    onClick: PropTypes.func,
    spacer: PropTypes.bool
  }),
  actions: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node,
  className: PropTypes.string,
  hideOnSelect: PropTypes.bool,
  iconOnly: PropTypes.bool,
  id: PropTypes.string,
  menuAlignment: PropTypes.string,
  menuLabel: PropTypes.string,
  textOnly: PropTypes.bool,
  show: PropTypes.oneOfType([PropTypes.oneOf([SHOW_ALL]), PropTypes.number]),
  variant: PropTypes.oneOf([null, VARIANT_PANEL_LIST, VARIANT_LIST])
};
Actions.defaultProps = {
  actions: [],
  action: undefined,
  id: v4(),
  menuAlignment: RIGHT,
  menuLabel: MORE_LABEL,
  show: 2,
  textOnly: false,
  iconOnly: false,
  className: '',
  children: null,
  variant: null,
  hideOnSelect: false
};
Actions.displayName = 'Actions';

export { Action, Spacer, Actions as default };
//# sourceMappingURL=index.js.map
